import React, { Component } from "react";
import axios from "axios";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    Link as MuiLink,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Snackbar,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    Close as CloseIcon,
    OpenInNew,
    Refresh as RefreshIcon,
    Save as SaveIcon,
    Warning as WarningIcon,
} from "@mui/icons-material";

import Api from "../../customlib/Api";
import Global from "../../customlib/Global";

/**
 * Customer Profile Panel (PRD v1.3.0 — gambar ke-5).
 *
 * Panel yang tampil di sisi kanan chat room CS. Isi:
 *   - Last Order Info (foto produk, nama pengirim, pembayaran, tax type,
 *     nomor CO klikable ke Lavender, harga non tax, qty)
 *   - Kategori Bantuan + Nomor Ticket (dari CsTicket)
 *   - List bantuan (notes tiket yang customer tulis)
 *   - Dropdown Isu CO (required) — CS wajib pilih sebelum close
 *
 * Props:
 *   - customerWaNumber : string required — nomor WA customer yang sedang di-chat
 *   - onClose          : () => void   — tombol close panel
 *   - onSnack          : ({sev,msg}) => void — optional, untuk snackbar feedback
 *
 * Polling: re-fetch setiap 15 detik supaya status tiket terupdate.
 */
class CustomerProfilePanel extends Component {
    state = {
        loading:   true,
        error:     null,
        data:      null,      // full response payload
        coIssue:   "",        // current dropdown value
        saving:    false,
        snack:     null,      // { sev, msg }
    };

    pollTimer = null;
    abortCtl  = null;

    componentDidMount() {
        this.load({ silent: false });
        this.pollTimer = setInterval(() => this.load({ silent: true }), 60000);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.customerWaNumber !== this.props.customerWaNumber) {
            this.load({ silent: false });
        }
    }

    componentWillUnmount() {
        clearInterval(this.pollTimer);
        if (this.abortCtl) this.abortCtl.abort();
    }

    load = ({ silent } = {}) => {
        const { customerWaNumber } = this.props;
        if (!customerWaNumber) {
            this.setState({ loading: false, data: null });
            return;
        }

        if (this.abortCtl) this.abortCtl.abort();
        const controller = new AbortController();
        this.abortCtl = controller;

        if (!silent) this.setState({ loading: true, error: null });

        axios
            .get(
                Api.csTicketCustomerProfile(customerWaNumber),
                { ...Api.getRequestHeader(), signal: controller.signal }
            )
            .then((res) => {
                if (res.data && res.data.success) {
                    const d = res.data.data;
                    this.setState({
                        loading: false,
                        data:    d,
                        // coIssue: (d.ticket && d.ticket.co_issue) || "",
                    });

                    if(!silent){
                        this.setState({
                            loading: false,
                            data:    d,
                            coIssue: (d.ticket && d.ticket.co_issue) || "",
                        });
                    }
                } else {
                    this.setState({ loading: false, error: res.data?.message || "Unknown error" });
                }
            })
            .catch((err) => {
                if (err.name !== "CanceledError" && err.name !== "AbortError") {
                    this.setState({ loading: false, error: err.message });
                }
            });
    };

    handleChangeCoIssue = (e) => {
        this.setState({ coIssue: e.target.value });
    };

    handleSaveCoIssue = () => {
        const { data, coIssue } = this.state;
        if (!data || !data.ticket) {
            this.setState({ snack: { sev: "warning", msg: "Tidak ada tiket aktif" } });
            return;
        }
        if (!coIssue) {
            this.setState({ snack: { sev: "warning", msg: "Pilih Isu CO dulu" } });
            return;
        }

        this.setState({ saving: true });
        axios
            .post(Api.csTicketSetCoIssue(data.ticket.id), { co_issue: coIssue }, Api.getRequestHeader())
            .then((res) => {
                this.setState({ saving: false });
                if (res.data && res.data.success) {
                    this.setState({ snack: { sev: "success", msg: "Isu CO tersimpan" } });
                    this.load({ silent: true });
                } else {
                    this.setState({ snack: { sev: "error", msg: res.data?.message || "Gagal simpan" } });
                }
            })
            .catch((err) => {
                this.setState({ saving: false, snack: { sev: "error", msg: err.message } });
            });
    };

    handleCloseTicket = (ignoreCoIssue = false) => {
        const { data, coIssue } = this.state;
        if (!data || !data.ticket) return;

        if(!ignoreCoIssue){
            if (!coIssue) {
                this.setState({
                    snack: { sev: "warning", msg: "Isu CO wajib diisi sebelum tutup tiket" },
                });
                return;
            }
        }

        this.setState({ saving: true });
        // Simpan co_issue dulu, baru close
        axios
            .post(Api.csTicketSetCoIssue(data.ticket.id), { co_issue: coIssue }, Api.getRequestHeader())
            .then(() =>
                axios.post(
                    Api.csTicketClose(data.ticket.id),
                    { note: "Closed via customer profile panel" },
                    Api.getRequestHeader()
                )
            )
            .then((res) => {
                this.setState({ saving: false });
                if (res.data && res.data.success) {
                    this.setState({ snack: { sev: "success", msg: "Tiket ditutup" } });
                    this.load({ silent: true });
                } else {
                    this.setState({ snack: { sev: "error", msg: res.data?.message || "Gagal tutup" } });
                }
            })
            .catch((err) => {
                this.setState({ saving: false, snack: { sev: "error", msg: err.message } });
            });
    };

    formatRupiah(n) {
        if (n === null || n === undefined) return "-";
        return "Rp " + Number(n).toLocaleString("id-ID");
    }

    renderProductImage(imagePath) {
        if (!imagePath) {
            return (
                <Box
                    sx={{
                        height: 120,
                        bgcolor: "grey.200",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 1,
                        mb: 1,
                    }}
                >
                    <Typography variant="caption" color="text.secondary">
                        No image
                    </Typography>
                </Box>
            );
        }

        // imagePath bisa berupa relative path dari Lavender, abs URL, atau nama file
        let src = imagePath;
        if (!/^https?:\/\//i.test(src)) {
            const base = Api.lavenderUrl || "https://lavender.prestisa.id";
            src = base.replace(/\/$/, "") + (src.startsWith("/") ? src : "/" + src);
        }

        return (
            <Box
                component="img"
                src={src}
                alt="product"
                sx={{
                    width: "100%",
                    maxHeight: 140,
                    objectFit: "contain",
                    borderRadius: 1,
                    mb: 1,
                    bgcolor: "grey.50",
                }}
                onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = "none";
                }}
            />
        );
    }

    renderLoadingSkeleton() {
        return (
            <Box sx={{ py: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <CircularProgress size={16} thickness={5} />
                    <Typography variant="caption" color="text.secondary">
                        Memuat data customer...
                    </Typography>
                </Stack>

                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                    Last Order Info
                </Typography>
                <Skeleton variant="rounded" height={120} sx={{ mb: 1, mt: 0.5 }} />
                <Skeleton variant="rounded" width={90} height={20} sx={{ mb: 1.5, borderRadius: "10px" }} />
                <Grid container spacing={1}>
                    {Array.from({ length: 4 }).map((_, i) => (
                        <Grid item xs={6} key={i}>
                            <Skeleton variant="text" width="60%" height={14} />
                            <Skeleton variant="text" width="80%" height={18} />
                        </Grid>
                    ))}
                </Grid>

                <Divider sx={{ my: 1.5 }} />

                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                    Kategori Bantuan
                </Typography>
                <Skeleton variant="text" width="70%" height={20} sx={{ mt: 0.5 }} />
                <Skeleton variant="text" width="40%" height={14} />
                <Skeleton variant="rounded" width={100} height={22} sx={{ mt: 0.5, borderRadius: "12px" }} />

                <Divider sx={{ my: 1.5 }} />

                <Typography variant="overline" color="error" sx={{ fontWeight: 700 }}>
                    Issue CO (dropdown) *Required
                </Typography>
                <Skeleton variant="rounded" height={40} sx={{ mt: 0.5, mb: 1 }} />
                <Skeleton variant="rounded" height={32} />
            </Box>
        );
    }

    render() {
        const { loading, error, data, coIssue, saving, snack } = this.state;
        const { onClose, customerWaNumber } = this.props;

        return (
            <Paper
                elevation={2}
                sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderLeft: "1px solid #e0e0e0",
                    paddingBottom: "30px",
                }}
            >
                {/* Header */}
                <Box sx={{ pl: "10px", pr: "10px", borderBottom: "1px solid #d0cece", bgcolor: "#ffffff", textAlign: "right" }}>
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={() => this.load({ silent: false })}>
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    {/* <Stack direction="row" alignItems="end" justifyContent="space-between">
                        <Typography variant="subtitle2" fontWeight={700}>
                            Customer Profile
                        </Typography>
                        <Stack direction="row" spacing={0.5} alignItems="right">
                            <Tooltip title="Refresh">
                                <IconButton size="small" onClick={() => this.load({ silent: false })}>
                                    <RefreshIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            {onClose && (
                                <Tooltip title="Close panel">
                                    <IconButton size="small" onClick={onClose}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Stack>
                    </Stack> */}
                    {/* <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                        {customerWaNumber}
                    </Typography> */}
                </Box>

                {/* Body */}
                <Box sx={{ flex: 1, overflowY: "auto", pl: 1.5, pr: 1.5 }}>
                    {loading && this.renderLoadingSkeleton()}

                    {error && (
                        <Alert severity="error" sx={{ mb: 1 }}>
                            {error}
                        </Alert>
                    )}

                    {!loading && !error && data && (
                        <>
                            {/* === Last Order Info === */}
                            {data.order ? (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                                        Last Order Info
                                    </Typography>
                                    {this.renderProductImage(data.order.product_image)}
                                    {data.order.product_code && (
                                        <Chip
                                            label={data.order.product_code}
                                            size="small"
                                            sx={{ mb: 1 }}
                                        />
                                    )}
                                    <Grid container spacing={1} sx={{ fontSize: 12 }}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">
                                                Nama Pengirim
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontSize: 12 }}>
                                                {data.order.sender_name || "-"}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">
                                                Pembayaran
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontSize: 12 }}>
                                                {data.order.payment_method || "-"}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">
                                                Tax Type
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontSize: 12 }}>
                                                {data.order.tax_type || "-"}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">
                                                Qty
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontSize: 12 }}>
                                                {data.order.qty || 0}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="caption" color="text.secondary">
                                                Nomor CO
                                            </Typography>
                                            <Box>
                                                {data.order.lavender_url ? (
                                                    <MuiLink
                                                        href={data.order.lavender_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        underline="hover"
                                                        sx={{ fontFamily: "monospace", fontSize: 12 }}
                                                    >
                                                        {data.order.order_number}
                                                        <OpenInNew sx={{ fontSize: 11, ml: 0.5, verticalAlign: "text-top" }} />
                                                    </MuiLink>
                                                ) : (
                                                    <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                                                        {data.order.order_number}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">
                                                Harga Non Tax
                                            </Typography>
                                            <Typography variant="body2" fontWeight={600} sx={{ fontSize: 12 }}>
                                                {this.formatRupiah(data.order.total_non_tax)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">
                                                Total
                                            </Typography>
                                            <Typography variant="body2" fontWeight={600} sx={{ fontSize: 12 }}>
                                                {this.formatRupiah(data.order.total)}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            ) : (
                                <Alert severity="warning" icon={<WarningIcon fontSize="inherit" />} sx={{ mb: 2, fontSize: 11 }}>
                                    Tidak ada order aktif untuk customer ini.
                                </Alert>
                            )}

                            <Divider sx={{ my: 1.5 }} />

                            {/* === Ticket Info === */}
                            {data.ticket ? (
                                <Box sx={{ mb: 2 }}>
                                    <Grid container justifyContent="space-between" alignItems="center">
                                        <Grid item>
                                            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                                                Kategori Bantuan
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <Typography
                                                sx={{
                                                    fontWeight: 700,
                                                    fontSize: 16,
                                                    fontFamily: "monospace",
                                                    color: data.ticket.status === "sedang_ditangani" ? "success.main" : "error.main",
                                                }}
                                            >
                                                {data.ticket.queue_label}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
                                        {this.humanizeCategory(data.ticket.category)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                                        {data.ticket.ticket_number}
                                    </Typography>
                                    <Box sx={{ mt: 0.5 }}>
                                        <Chip
                                            size="small"
                                            label={this.humanizeStatus(data.ticket.status)}
                                            color={data.ticket.status === "sedang_ditangani" ? "success" : "warning"}
                                        />
                                    </Box>

                                    {/* List bantuan = ticket notes */}
                                    {data.ticket.notes && (
                                        <Box sx={{ mt: 1 }}>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                List bantuan:
                                            </Typography>
                                            <Box
                                                sx={{
                                                    whiteSpace: "pre-wrap",
                                                    fontFamily: "monospace",
                                                    fontSize: 11,
                                                    p: 1,
                                                    bgcolor: "grey.100",
                                                    borderRadius: 1,
                                                    maxHeight: 100,
                                                    overflowY: "auto",
                                                }}
                                            >
                                                {data.ticket.notes}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            ) : (
                                <Alert severity="info" sx={{ mb: 2, fontSize: 11 }}>
                                    Tidak ada tiket aktif untuk customer ini.
                                </Alert>
                            )}

                            <Divider sx={{ my: 1.5 }} />

                            {/* === Isu CO dropdown (required) === */}
                            {data.ticket && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="overline" color="error" sx={{ fontWeight: 700 }}>
                                        Issue CO (dropdown) *Required
                                    </Typography>
                                    <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                                        <InputLabel id="co-issue-label">Pilih Isu</InputLabel>
                                        <Select
                                            labelId="co-issue-label"
                                            label="Pilih Isu"
                                            value={coIssue}
                                            onChange={this.handleChangeCoIssue}
                                            disabled={data?.ticket?.status === "selesai"}
                                        >
                                            <MenuItem value="">— pilih —</MenuItem>
                                            {(data.co_issue_options || []).map((opt) => (
                                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<SaveIcon />}
                                            disabled={saving || !coIssue || data?.ticket?.status === "selesai"}
                                            onClick={this.handleSaveCoIssue}
                                            fullWidth
                                            sx={{textTransform: "capitalize", mt: "10px"}}
                                        >
                                            Simpan
                                        </Button>

                                        <Divider sx={{ my: 1.5 }} />

                                        <Box>
                                            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                                                Detail Complaint (CO)
                                            </Typography>

                                            <Box>
                                                <Typography variant="body2" sx={{ fontSize: 12 }}>
                                                    <strong>Order : </strong>
                                                    {data?.order?.problem?.order_number}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontSize: 12 }}>
                                                    <strong>Kategori CO : </strong>
                                                    {data?.order?.problem?.complaint_category}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontSize: 12 }}>
                                                    <strong>Catatan : </strong>
                                                    {data?.order?.problem?.notes}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Divider sx={{ my: 1.5 }} />

                                        {/* {(Global.isCustomerService() || Global.isCustomerServiceSpv()) && data.ticket.status === "sedang_ditangani" && ( */}
                                            <Button
                                                size="small"
                                                variant="contained"
                                                color="success"
                                                disabled={saving || !coIssue || (!Global.isCustomerService() && !Global.isCustomerServiceSpv()) || data.ticket.status !== "sedang_ditangani"}
                                                onClick={() => this.handleCloseTicket(false)}
                                                fullWidth
                                                sx={{textTransform: "capitalize"}}
                                            >
                                                Tutup Tiket
                                            </Button>
                                        {/* )} */}
                                        {/* {`${saving} - ${!coIssue} - ${!Global.isCustomerService()} - ${!Global.isCustomerServiceSpv()} - ${data.ticket.status !== "sedang_ditangani"} - ${(!Global.isCustomerService() && !Global.isCustomerServiceSpv()) || data.ticket.status !== "sedang_ditangani"}`} */}
                                        {(!Global.isCustomerService() || !Global.isCustomerServiceSpv()) && (
                                            <Button
                                                size="small"
                                                variant="contained"
                                                color="success"
                                                disabled={saving || data?.ticket?.status === "selesai"}
                                                onClick={() => this.handleCloseTicket(true)}
                                                fullWidth
                                                sx={{mt: "20px", textTransform: "capitalize"}}
                                            >
                                                Tandai Review Selesai
                                            </Button>
                                        )}

                                    <Stack direction="column" spacing={1} sx={{ mt: 1 }}>
                                        
                                    </Stack>
                                </Box>
                            )}
                        </>
                    )}
                </Box>

                <Snackbar
                    open={!!snack}
                    autoHideDuration={3500}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    onClose={() => this.setState({ snack: null })}
                >
                    {snack ? <Alert severity={snack.sev}>{snack.msg}</Alert> : undefined}
                </Snackbar>
            </Paper>
        );
    }

    humanizeCategory(cat) {
        const map = {
            komplain:         "Complaint",
            tracking_order:   "Tracking Order",
            konfirmasi_order: "Konfirmasi Order",
        };
        return map[cat] || cat;
    }

    humanizeStatus(status) {
        const map = {
            menunggu:         "Menunggu",
            sedang_ditangani: "Sedang Ditangani",
            selesai:          "Selesai",
            expired:          "Kadaluwarsa",
        };
        return map[status] || status;
    }
}

export default CustomerProfilePanel;
